import * as React from "react";
import { inject, observer } from "mobx-react";
import { WizardFlightPWAProps } from "./typings";
import { withRouter } from "react-router";
import { locKeys } from "components/flexComponents/WizardFlightPWA/components/l10n";
import { WizardFlightPWAView } from "./WizardFlightPWAView";
import { FlightConfig } from "../../../stores/wizard/config/staticConfigs/flight";
import { FlightWizardState } from "../../../stores/wizard/state/flight";
import { useLocalization } from "@shared-ui/localization-context";
import { ClassCodes } from "src/components/shared/PreferredClassInput/PreferredClassInput";

//for URL construction
const flightClassCodeMap = {
  p: "cabinclass:premium",
  b: "cabinclass:business",
  f: "cabinclass:first",
  e: "cabinclass:economy",
  default: "cabinclass:economy",
};

@inject("wizardState", "routeStore", "context")
@observer
export class WizardFlightPWA extends React.Component<WizardFlightPWAProps> {
  public updateSearch = (pwaSubLOB: string) => {
    this.props.routeStore.updateSearch({ pwaSubLOB });
  };

  public onSelectPreferredClassCode = (classCode: ClassCodes) => {
    this.props.wizardState.flightWizardState.setFlightClass(classCode);
  };

  private setFlightType = () => {
    const { formatText } = this.props.l10n;
    if (this.props.routeStore.queryParams.pwaSubLOB) {
      if (this.props.routeStore.queryParams.pwaSubLOB === "oneway") {
        this.props.wizardState.flightWizardState.updateSubLOBState("oneway");

        return formatText(locKeys.flightOneWayText);
      } else if (this.props.routeStore.queryParams.pwaSubLOB === "multicity") {
        this.props.wizardState.flightWizardState.updateSubLOBState("multicity");

        return formatText(locKeys.flightMultiCityText);
      }
    }
    this.props.wizardState.flightWizardState.updateSubLOBState("");

    return formatText(locKeys.flightRoundTripText);
  };

  private getFlightURL = (param: string, value: string) => (): string => {
    const params = { ...this.props.routeStore.queryParams };
    params[param] = value;
    const paramsUrl = new URLSearchParams(params).toString();

    return `${this.props.routeStore.pathname}?${paramsUrl}`;
  };

  private getParam = (param: string) => (): string | undefined => {
    return this.props.routeStore.queryParams[param];
  };

  //Gets the URL fragment for the "main" flight; if the multi city option is selected, additional legs are added to the URL via another path
  public static getLegURLFragment(flightWizardState: FlightWizardState, flightConfig: FlightConfig): string[] {
    const legs: string[] = new Array(2);

    const rawFormatForSubmission = flightConfig.date.ISOSubmissionSupport
      ? flightConfig.date.format
      : flightWizardState.localDateFormat;

    const { formatDate } = useLocalization();

    const from = "from:";
    const origin = flightWizardState.location.origin.value;
    const comma = ",";
    const to = "to:";
    const destination = flightWizardState.location.destination.value;
    const departure = "departure:";
    const startDate = formatDate(flightWizardState.date.start, {
      raw: rawFormatForSubmission,
    });
    const departureTime = "TANYT";

    let leg1;
    leg1 = from + origin + comma + to + destination + comma + departure + startDate + departureTime;
    legs[0] = leg1;

    if (flightWizardState.subLOBState === "") {
      // if flightWizardState.subLOBState == "" so it's a roundtrip flight.
      let leg2;
      const endDate = formatDate(flightWizardState.date.end, {
        raw: rawFormatForSubmission,
      });
      leg2 = from + destination + comma + to + origin + comma + departure + endDate + departureTime;
      legs[1] = leg2;
    }

    return legs;
  }

  public static getTripValueForURL(flightWizardState: FlightWizardState): string {
    const flightType = flightWizardState.subLOBState;

    if (flightType === "multicity") {
      return "multi";
    } else if (flightType === "") {
      return "roundtrip";
    } else {
      return flightType;
    }
  }

  public static getFlightClassCodeForURL(flightClassCode: ClassCodes): string {
    return flightClassCodeMap[flightClassCode] || flightClassCodeMap.default;
  }

  constructor(props: WizardFlightPWAProps) {
    super(props);
    const flightWizardState = this.props.wizardState.flightWizardState;
    flightWizardState.overrideConfig(() => {
      flightWizardState.config.elementId = this.props.flexComponent.id;
      flightWizardState.config.enableAddHotel = this.props.flexComponent.model.enableAddHotel;
      flightWizardState.config.enableAddCar = this.props.flexComponent.model.enableAddCar;
      flightWizardState.config.heading.fixedTitle = this.props.flexComponent.model.fixedLobTitle;
      flightWizardState.config.lastSearch.useLastSearchForSingleLOB = this.props.flexComponent.model.useLastSearchForSingleLOB;
      flightWizardState.config.heading.lobSubtitleBreakPoint = this.props.flexComponent.model.lobSubtitleBreakPoint;
      flightWizardState.config.heading.hideLobTitle = this.props.flexComponent.model.hideLobTitle;
    });
    flightWizardState.updateStateFromCookie();
    flightWizardState.updateDateFromConfig();
    this.props.wizardState.flightWizardState.localDateFormat = this.props.context.localDateFormat;
    if (flightWizardState.subLOBState) {
      this.props.routeStore.updateSearch({ pwaSubLOB: flightWizardState.subLOBState });
    }
  }

  public render() {
    return (
      <WizardFlightPWAView
        {...this.props}
        setFlightType={this.setFlightType}
        updateSubLobInSearch={this.updateSearch}
        onSelectPreferredClassCode={this.onSelectPreferredClassCode}
        getFlightURL={this.getFlightURL}
        getParam={this.getParam}
      />
    );
  }
}

export default withRouter(WizardFlightPWA);
